import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App';
import { initLiff } from './lib/liff';
import { hasPersistedJwt } from './lib/store';
import { ToastProvider } from './components/ui/toast';
import { ConfirmProvider } from './components/ui/confirm';
import { ConsequenceProvider } from './components/ui/ConsequencePreview';
import { ErrorBoundary } from './components/ErrorBoundary';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

async function bootstrap() {
  const root = document.getElementById('root');
  if (!root) throw new Error('No #root element');

  // Web-login flow must bypass LIFF entirely — initLiff() redirects to LINE
  // OAuth when there's no LINE session, trapping desktop/web users on a bare
  // URL. Skip it for explicit web sessions AND for any plain browser that has
  // no LIFF context: only enter the LINE flow when we're actually inside the
  // LINE app (UA) or returning from a LIFF OAuth round-trip (liff params).
  const path = window.location.pathname;
  const ua = navigator.userAgent || '';
  const inLineApp = /\bLine\//i.test(ua);
  const sp = new URLSearchParams(window.location.search);
  const hasLiffParams =
    sp.has('liffClientId') ||
    sp.has('liffRedirectUri') ||
    sp.has('liff.state') ||
    (sp.has('code') && sp.has('state'));
  const webOnly =
    path === '/login' || hasPersistedJwt() || (!inLineApp && !hasLiffParams);

  if (!webOnly) {
    try {
      await initLiff();
    } catch (e) {
      console.error('LIFF init failed:', e);
    }
  }

  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ConfirmProvider>
            <ConsequenceProvider>
              <BrowserRouter>
                <ErrorBoundary level="app">
                  <App />
                </ErrorBoundary>
              </BrowserRouter>
            </ConsequenceProvider>
          </ConfirmProvider>
        </ToastProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
